import { useCallback, useEffect, useRef, useState } from 'react';

import { DisplaySymbol, DisplaySymbolMap, formatInches, FRACTION_MAP } from '@core';
import { CorridorParameterMargins } from '@data';

import { colors } from '../../colors';
import { u } from '../../styles';
import { typography } from '../../typography';
import { CaretLeftIcon, CaretRightIcon, TrashIcon } from '../icons';
import { Box } from '../Layout';
import { ElvaLogo } from '../Logo/ElvaLogo';

import * as css from './GoalParameterSet.css';
import { useGoalRange } from './useGoalRange';

export type GoalParameterSetProps = {
    /** Disables editing and hides stepper controls when true. */
    readOnly?: boolean;

    /** Title displayed in the card header. */
    title: string;

    /** Symbol used to format and display numeric values (e.g. °, mph, in). */
    displaySymbol: DisplaySymbol;

    /** Lower bound of the goal range (controlled by parent, may be undefined). */
    min?: number;

    /** Upper bound of the goal range (controlled by parent, may be undefined). */
    max?: number;

    /** Optional corridor visualization showing inner min/max reference values. */
    corridor?: CorridorParameterMargins;

    /** Called when min or max changes; parent owns and controls the range state. */
    onUpdate?: (params: { min?: number; max?: number }) => void;

    /** Optional delete handler; shows trash button when provided. */
    onDelete?: () => void;

    /** Step size used for increment/decrement controls. @default 1 */
    stepper?: number;

    variant?: 'primary' | 'secondary';

    /** Optional delete handler; shows trash button when provided. */
    onSelect?: () => void;

    isSelected?: boolean;
};

export function GoalParameterSet({
    title,
    min,
    max,
    displaySymbol,
    corridor,
    readOnly,
    onUpdate,
    onDelete,
    onSelect,
    stepper = 1,
    variant = 'secondary',
    isSelected,
}: GoalParameterSetProps) {
    const range = useGoalRange({
        min,
        max,
        step: stepper,
        onUpdate,
    });
    return (
        <article className={css.card[variant]} onClick={onSelect}>
            <header className={css.header}>
                <Box display="flex" gap={u(0.5)} alignItems="center">
                    {onSelect && (
                        <input type="checkbox" className={css.input} checked={isSelected} onChange={() => {}} />
                    )}
                    <h4 className={typography({ variant: 'h4' })}>{title}</h4>
                </Box>
                <div className={css.headerOptions}>
                    {onDelete && (
                        <button onClick={onDelete}>
                            <TrashIcon size={20} color={colors.red[500]} />
                        </button>
                    )}
                </div>
            </header>

            <section className={css.content}>
                <RangeSelector
                    readOnly={readOnly}
                    value={min}
                    displaySymbol={displaySymbol}
                    isIncrementDisabled={!range.min.canIncrement}
                    isDecrementDisabled={!range.min.canDecrement}
                    onIncrement={range.min.increment}
                    onDecrement={range.min.decrement}
                    onUpdate={range.min.update}
                />

                <RangeSelector
                    readOnly={readOnly}
                    value={max}
                    displaySymbol={displaySymbol}
                    isIncrementDisabled={!range.max.canIncrement}
                    isDecrementDisabled={!range.max.canDecrement}
                    onIncrement={range.max.increment}
                    onDecrement={range.max.decrement}
                    onUpdate={range.max.update}
                />
            </section>

            <footer className={css.footer[variant]}>
                <ElvaLogo width={22} />
                <div className={css.footerViz}>
                    <span>{corridor?.innerMin ?? 0}</span>
                    <span className={css.footerBar} />
                    <span>{corridor?.innerMax ?? 0}</span>
                </div>
            </footer>
        </article>
    );
}

type RangeSelectorProp = {
    value?: number;
    displaySymbol: DisplaySymbol;
    isDecrementDisabled?: boolean;
    isIncrementDisabled?: boolean;
    onIncrement: () => void;
    onDecrement: () => void;
    onUpdate: (value?: number) => void;
    readOnly?: boolean;
    stepper?: number;
};

const RangeSelector = ({
    value,
    displaySymbol,
    onDecrement,
    onIncrement,
    isDecrementDisabled,
    isIncrementDisabled,
    readOnly,
    onUpdate,
    stepper = 1,
}: RangeSelectorProp) => {
    return (
        <div className={css.rangeSelector.root({ readOnly: readOnly })}>
            {!readOnly && (
                <button className={css.rangeSelector.button} onClick={onDecrement} disabled={isDecrementDisabled}>
                    <CaretLeftIcon size={24} color={isDecrementDisabled ? colors.bluegray[300] : colors.blue[500]} />
                </button>
            )}

            <RenderValue value={value} displaySymbol={displaySymbol} onUpdate={onUpdate} readOnly={readOnly} />

            {!readOnly && (
                <button className={css.rangeSelector.button} onClick={onIncrement} disabled={isIncrementDisabled}>
                    <CaretRightIcon size={24} color={isIncrementDisabled ? colors.bluegray[300] : colors.blue[500]} />
                </button>
            )}
        </div>
    );
};

type RenderValueProps = Pick<RangeSelectorProp, 'displaySymbol' | 'value' | 'readOnly'> & {
    onUpdate: (value?: number) => void;
};

const RenderValue = ({ value, displaySymbol, onUpdate, readOnly }: RenderValueProps) => {
    const ref = useRef<HTMLInputElement>(null);
    const [edit, setEdit] = useState(false);
    useEffect(() => {
        if (edit) {
            ref?.current?.focus();
        }
    }, [edit, ref]);

    const openForm = useCallback(() => {
        !readOnly && setEdit(true);
    }, [setEdit, readOnly]);

    const handleBlur = useCallback(() => {
        setEdit(false);
        // if the number is illegal state we need to correct it
    }, [setEdit]);

    if (edit) {
        return (
            <div className={css.rangeSelector.inputRoot}>
                <input
                    ref={ref}
                    className={css.rangeSelector.input}
                    onBlur={handleBlur}
                    type="number"
                    onChange={(e) => {
                        onUpdate(e.target.value ? +e.target.value : undefined);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Escape') {
                            ref.current?.blur();
                        }
                    }}
                    value={value ?? ''}
                />
            </div>
        );
    }
    if (typeof value === 'undefined') {
        return (
            <div className={css.rangeSelector.inputRoot} onClick={openForm}>
                {`- `}
                {displaySymbol}
            </div>
        );
    }
    if (displaySymbol !== DisplaySymbolMap.in) {
        return (
            <div className={css.rangeSelector.inputRoot} onClick={openForm}>
                {value}
                {displaySymbol}
            </div>
        );
    }
    const { quarterFraction, isNegative, integer } = formatInches(value);

    return (
        <div className={css.rangeSelector.inputRoot} onClick={openForm}>
            {isNegative && '-'}
            {integer}
            {quarterFraction ? <span style={{ fontSize: '0.95em' }}>{FRACTION_MAP[quarterFraction]}</span> : null}
            {displaySymbol}
        </div>
    );
};
